import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import './SlideshowModal.css';
import {
  formatRelativeTime,
  getFullDate,
} from "../../utils/formatUtils";

const getUserInitials = (userName) => {
  if (!userName) return '?';
  return userName
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return tmp.textContent || tmp.innerText || '';
};
const truncateHtmlToText = (html, maxLength = 120) => {
  if (!html) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  let text = tempDiv.textContent || tempDiv.innerText || '';
  text = text.replace(/\s+/g, ' ').trim();
  
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

const processInlineImagesInContent = (html, inlineImages = []) => {
  if (!html) return '';
  
  let processedHtml = html;
  
  const imageMap = new Map();
  inlineImages.forEach(img => {
    if (img.id) imageMap.set(img.id.toString(), img);
    if (img.fileName) imageMap.set(img.fileName, img);
  });
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = processedHtml;
  
  tempDiv.querySelectorAll('img').forEach(img => {
    const src = img.getAttribute('src') || '';
    const alt = img.getAttribute('alt') || '';
    
    let matchedImage = null;
    
    if (src.includes('/api/inlineimages/')) {
      const match = src.match(/\/api\/inlineimages\/GetInlineImage\/(\d+)/);
      if (match && match[1]) {
        const imageId = match[1];
        matchedImage = imageMap.get(imageId);
        
        if (matchedImage && matchedImage.url) {
          img.setAttribute('src', matchedImage.url);
          img.setAttribute('data-image-id', imageId);
          img.classList.add('inline-image-loaded');
          
          img.style.opacity = '0.6';
          img.onload = () => { img.style.opacity = '1'; };
          img.onerror = () => {
            console.error(`Failed to load image ${imageId}`);
            img.src = 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23999\' stroke-width=\'2\'%3E%3Crect x=\'2\' y=\'2\' width=\'20\' height=\'20\'/%3E%3C/svg%3E';
            img.style.opacity = '0.3';
          };
        }
      }
    }
    
    const dataImageId = img.getAttribute('data-image-id');
    if (!matchedImage && dataImageId) {
      matchedImage = imageMap.get(dataImageId);
      if (matchedImage && matchedImage.url) {
        img.setAttribute('src', matchedImage.url);
        img.classList.add('inline-image-loaded');
      }
    }
    
    if (!matchedImage && alt && alt.match(/image[_\s]?id:?(\d+)/i)) {
      const match = alt.match(/(\d+)/);
      if (match && match[1]) {
        matchedImage = imageMap.get(match[1]);
        if (matchedImage && matchedImage.url) {
          img.setAttribute('src', matchedImage.url);
          img.classList.add('inline-image-loaded');
        }
      }
    }
    
    if (matchedImage && matchedImage.url) {
      img.style.cursor = 'pointer';
      img.addEventListener('click', (e) => {
        e.stopPropagation();
        window.open(matchedImage.url, '_blank');
      });
    }
  });
  
  tempDiv.querySelectorAll('[data-inline-image-id]').forEach(el => {
    const imageId = el.getAttribute('data-inline-image-id');
    const image = imageMap.get(imageId);
    
    if (image && image.url) {
      const img = document.createElement('img');
      img.src = image.url;
      img.alt = image.fileName || 'Inline Image';
      img.style.maxWidth = '100%';
      img.style.borderRadius = '4px';
      img.setAttribute('data-image-id', imageId);
      el.parentNode.replaceChild(img, el);
    }
  });
  
  return tempDiv.innerHTML;
};

const formatNoteContent = (html, noteInlineImages = []) => {
  if (!html && noteInlineImages.length === 0) return 'No content';
  
  let content = html || '';
  
  if (noteInlineImages && noteInlineImages.length > 0) {
    content = processInlineImagesInContent(content, noteInlineImages);
  }
  
  if (noteInlineImages && noteInlineImages.length > 0) {
    console.log('Rendering inline images gallery:', noteInlineImages);
    
    let galleryHtml = `
      <div class="unified-media-gallery">
        
        <div class="gallery-grid">
    `;
    
    noteInlineImages.forEach(img => {
      galleryHtml += `
        <div class="gallery-item image-item" data-url="${img.url}" data-type="image">
          <div class="gallery-item-preview">
            <img src="${img.url}" 
                 alt="${escapeHtml(img.fileName || 'Inline Image')}" 
                 class="gallery-item-thumbnail"
                 loading="lazy"
                 onerror="this.src='data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23999\' stroke-width=\'2\'%3E%3Crect x=\'2\' y=\'2\' width=\'20\' height=\'20\'/%3E%3C/svg%3E'" />
            <div class="gallery-item-overlay">
              <i class="fas fa-search-plus"></i>
            </div>
          </div>
          <div class="gallery-item-info">
            <div class="gallery-item-name" title="${escapeHtml(img.fileName || 'Inline Image')}">
              ${escapeHtml((img.fileName || 'Inline Image').length > 40 ? (img.fileName || 'Inline Image').substring(0, 37) + '...' : (img.fileName || 'Inline Image'))}
            </div>
            
          </div>
        </div>
      `;
    });
    
    galleryHtml += `
        </div>
      </div>
    `;
    
    if (content && content !== 'No content') {
      content = `<div class="slideshow-content-wrapper">${content}</div>${galleryHtml}`;
    } else {
      content = galleryHtml;
    }
  }
  
  return content;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};

const escapeHtml = (str) => {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const SlideshowModal = ({ isOpen, onClose, notes, currentNoteIndex = 0, jobName, showTaskName = true, taskName: taskNameProp }) => {
  const [currentIndex, setCurrentIndex] = useState(currentNoteIndex);
  const [direction, setDirection] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const controlsTimeoutRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [inlineImages, setInlineImages] = useState({}); 
  const [loadingInlineImages, setLoadingInlineImages] = useState(false);

  const currentNote = notes[currentIndex];
  const previousNote = currentIndex > 0 ? notes[currentIndex - 1] : null;
  const nextNote = currentIndex < notes.length - 1 ? notes[currentIndex + 1] : null;

  const memoizedMainContent = useMemo(() => {
    return formatNoteContent(
      currentNote?.note || currentNote?.content || '', 
      inlineImages[currentNote?.id] || []
    );
  }, [currentNote, inlineImages]);

  const memoizedPrevContent = useMemo(() => {
  if (!previousNote) return '';
  return truncateHtmlToText(
    previousNote?.note || previousNote?.content || '', 
    120
  );
}, [previousNote]);

  const handleContentElementClick = (e) => {
    const galleryItem = e.target.closest('.gallery-item');
    if (galleryItem) {
      e.stopPropagation();
      const targetUrl = galleryItem.getAttribute('data-url');
      const fileType = galleryItem.getAttribute('data-type');
      
      if (targetUrl) {
        if (fileType === 'image') {
          window.open(targetUrl, '_blank');
        }
      }
      return;
    }
    
    const interactiveElement = e.target.closest('.slideshow-image-item, img');
    if (!interactiveElement) return;

    e.stopPropagation();
    const targetUrl = interactiveElement.getAttribute('data-url') || interactiveElement.src;
    if (targetUrl) {
      window.open(targetUrl, '_blank');
    }
  };

  useEffect(() => {
    setCurrentIndex(currentNoteIndex);
  }, [currentNoteIndex]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (notes && notes.length > 0) {
      const noteIds = notes.map(n => n.id || n._id || n.noteId);
      const duplicates = noteIds.filter((id, index) => noteIds.indexOf(id) !== index);
      if (duplicates.length > 0) {
        console.error('Duplicate note IDs found in notes array:', duplicates);
      }
    }
  }, [notes]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          document.exitFullscreen();
        } else {
          onClose();
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      } else if (e.key === 'h' || e.key === 'H') {
        setShowControls(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, notes.length, isFullscreen]);

  // Auto-hide controls
  useEffect(() => {
    if (!isOpen) return;

    const resetTimer = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    };

    resetTimer();
    const handleMouseMove = () => resetTimer();
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(currentNoteIndex);
      setDirection(''); 
    }
  }, [isOpen, currentNoteIndex, notes]);

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev < notes.length - 1) return prev + 1;
          setIsPlaying(false); 
          return prev;
        });
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, notes.length]);

  const togglePlay = () => setIsPlaying(!isPlaying);

  const handleNext = useCallback(() => {
    if (currentIndex < notes.length - 1) {
      setDirection('next');
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, notes.length]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setDirection('prev');
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const fetchInlineImages = useCallback(async (noteId) => {
    if (!noteId) return [];
    try {
      console.log('Fetching inline images for note ID:', noteId);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/InlineImages/GetInlineImagesBySiteNote?siteNoteId=${noteId}`,
        { headers: { accept: "application/json" } }
      );
      if (!response.ok) throw new Error("Failed to load inline images");

      const data = await response.json();
      const images = Array.isArray(data.images) ? data.images : [];
      return images.map(img => ({
        ...img,
        url: `${process.env.REACT_APP_API_BASE_URL}/api/InlineImages/GetInlineImage/${img.id}`
      }));
    } catch (error) {
      console.error('Error fetching inline images:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    const loadNoteInlineImages = async () => {
      if (!isOpen || !currentNote) return;
      
      const currentNoteId = currentNote.id;
      if (!currentNoteId) return;

      const needInlineImages = !inlineImages[currentNoteId];

      if (needInlineImages) {
        setLoadingInlineImages(true);
        try {
          const imgs = await fetchInlineImages(currentNoteId);
          setInlineImages(prev => ({ ...prev, [currentNoteId]: imgs }));
        } catch (error) {
          console.error("Error loading inline images:", error);
        } finally {
          setLoadingInlineImages(false);
        }
      }
    };
    loadNoteInlineImages();
  }, [isOpen, currentIndex, currentNote, fetchInlineImages, inlineImages]);

  const toggleFullscreen = useCallback(() => {
    const element = document.querySelector('.slideshow-modal-content');
    if (!document.fullscreenElement) {
      element.requestFullscreen().catch(err => console.error(err));
    } else {
      document.exitFullscreen();
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div className="slideshow-modal-overlay" onClick={onClose}>
      <div className="slideshow-modal-content" onClick={e => e.stopPropagation()}>
        
        <div className={`slideshow-top-bar ${showControls ? 'visible' : 'hidden'}`}>
          <div className="slideshow-top-bar-left">
            <button className="slideshow-btn" onClick={togglePlay} title={isPlaying ? 'Pause Auto-play' : 'Play All'}>
              <i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
            </button>
            <button className="slideshow-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Exit Fullscreen (F)' : 'Fullscreen (F)'}>
              <i className={`fas fa-${isFullscreen ? 'compress' : 'expand'}`} />
            </button>
            <span className="slideshow-title">
              <i className="fas fa-images" /> {jobName || 'Notes'} - Slide {currentIndex + 1} of {notes.length}
            </span>
          </div>
          <div className="slideshow-top-bar-right">            
            <button className="slideshow-btn" onClick={onClose} title="Close (Esc)">
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        <div className="slideshow-body">
          {previousNote && (
            <div className={`slideshow-preview-card prev ${showControls ? 'visible' : 'hidden'}`} onClick={handlePrevious}>
              <div className="slideshow-note is-preview">
                <div className="task-accent-bar" />
                <div className="slideshow-note-header">
                  <div className="task-header-left">
                    <i className="fas fa-chevron-left" style={{marginRight: '8px'}}></i>
                    <span className="task-label">Previous</span>
                  </div>
                </div>
                <div className="slideshow-note-content preview-content">
                  <div dangerouslySetInnerHTML={{ __html: memoizedPrevContent }} />
                </div>
                <div className="task-footer">
                  <div className="slideshow-note-avatar">{getUserInitials(previousNote?.userName)}</div>
                  <div className="slideshow-note-user-details">
                    <span className="task-requested-by"><strong>{previousNote?.userName || 'Unknown'}</strong></span>
                    <span className="slideshow-note-date"><i className="far fa-clock"></i> {formatRelativeTime(previousNote?.date || previousNote?.timeStamp)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="slideshow-note-container">
            <div className="slideshow-note">
              <div className="task-accent-bar" />
              <div className="slideshow-note-header">
                <div className="task-header-left">
                  <i className="fas fa-clipboard-check task-icon"></i>
                  <div className="task-breadcrumb-wrapper">
                    <span className="breadcrumb-item" title="Workspace">{currentNote?.workspaceName || 'Workspace'}</span>
                    <i className="fas fa-chevron-right breadcrumb-sep"></i>
                    <span className="breadcrumb-item" title="Project">{currentNote?.projectName || 'Project'}</span>
                    <i className="fas fa-chevron-right breadcrumb-sep"></i>
                    <span className="breadcrumb-item" title="Job">{currentNote?.jobName || jobName || 'Job'}</span>
                    {showTaskName && (
                      <>
                        <i className="fas fa-chevron-right breadcrumb-sep"></i>
                        <span className="task-label active-breadcrumb">
                          {currentNote?.taskName || currentNote?.task?.title || currentNote?.title || ' '}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="slideshow-note-content main-content" onClick={handleContentElementClick}>
                <div dangerouslySetInnerHTML={{ __html: memoizedMainContent }} />
                
              </div>

              <div className="task-footer">
                <div className="slideshow-note-avatar">
                  {currentNote?.userImageUrl ? <img src={currentNote.userImageUrl} alt="User" /> : getUserInitials(currentNote?.userName)}
                </div>
                <div className="slideshow-note-user-details">
                  <span className="task-requested-by"><strong>{currentNote?.userName || 'Unknown'}</strong></span>
                  <span className="slideshow-note-date"><i className="far fa-clock"></i> {formatRelativeTime(currentNote?.date || currentNote?.timeStamp)}</span>
                </div>
              </div>
            </div>
          </div>

          {nextNote && (
            <div className={`slideshow-preview-card next ${showControls ? 'visible' : 'hidden'}`} onClick={handleNext}>
              <div className="slideshow-note is-preview">
                <div className="task-accent-bar" />
                <div className="slideshow-note-header">
                  <div className="task-header-right" style={{display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'flex-end'}}>
                    <span className="task-label" style={{marginRight: '8px'}}>Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </div>
                </div>
                <div className="task-main-text">
                  {stripHtml(nextNote?.note || nextNote?.content || '').substring(0, 80)}...
                </div>
                <div className="task-footer">
                  <div className="slideshow-note-avatar">{getUserInitials(nextNote?.userName)}</div>
                  <div className="slideshow-note-user-details">
                    <span className="task-requested-by"><strong>{nextNote?.userName || 'Unknown'}</strong></span>
                    <span className="slideshow-note-date"><i className="far fa-clock"></i> {formatRelativeTime(nextNote?.date || nextNote?.timeStamp)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <button className={`slideshow-nav prev ${showControls ? 'visible' : 'hidden'}`} onClick={handlePrevious} disabled={currentIndex === 0} title="Previous (←)">
            <i className="fas fa-chevron-left" />
          </button>
          <button className={`slideshow-nav next ${showControls ? 'visible' : 'hidden'}`} onClick={handleNext} disabled={currentIndex === notes.length - 1} title="Next (→)">
            <i className="fas fa-chevron-right" />
          </button>
        </div>

        <div className={`slideshow-footer ${showControls ? 'visible' : 'hidden'}`}>
          <div className="slideshow-thumbnails-container">
            <button className="thumbnails-nav prev" onClick={() => document.querySelector('.slideshow-thumbnails').scrollBy({ left: -200, behavior: 'smooth' })} title="Scroll left">
              <i className="fas fa-chevron-left" />
            </button>
            <div className="slideshow-thumbnails">
              {notes.map((note, idx) => (
                <div key={`thumbnail-${idx}`} className={`slideshow-thumbnail ${idx === currentIndex ? 'active' : ''}`} onClick={() => setCurrentIndex(idx)}>
                  <div className="thumbnail-number">{idx + 1}</div>
                  <div className="thumbnail-preview">{stripHtml(note.note || note.content || '').substring(0, 30)}...</div>
                </div>
              ))}
            </div>
            <button className="thumbnails-nav next" onClick={() => document.querySelector('.slideshow-thumbnails').scrollBy({ left: 200, behavior: 'smooth' })} title="Scroll right">
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        </div>

        <div className="slideshow-progress">
          <div className="slideshow-progress-bar" style={{ width: `${((currentIndex + 1) / notes.length) * 100}%` }} />
        </div>

        <div className="slideshow-slide-number">{currentIndex + 1} / {notes.length}</div>
      </div>
    </div>
  );
};

SlideshowModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notes: PropTypes.array.isRequired,
  currentNoteIndex: PropTypes.number,
  jobName: PropTypes.string,
  showTaskName: PropTypes.bool,
  taskName: PropTypes.string
};

export default SlideshowModal;